import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Upload, Loader2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { optimizeImageUpload } from '@/utils/imageOptimization';

interface AvatarUploadProps {
  currentAvatarUrl?: string | null;
  userDisplayName?: string;
  userId: string;
  onAvatarUpdate?: (url: string | null) => void;
}

export const AvatarUpload = ({ 
  currentAvatarUrl, 
  userDisplayName, 
  userId,
  onAvatarUpdate 
}: AvatarUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(currentAvatarUrl);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { toast } = useToast();

  const getInitials = () => {
    if (!userDisplayName) return '?';
    const names = userDisplayName.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return userDisplayName.slice(0, 2).toUpperCase();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 2MB for original)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image under 2MB',
        variant: 'destructive',
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async () => {
    if (!previewUrl) return;

    try {
      setUploading(true);

      // Convert preview to blob and optimize
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const file = new File([blob], 'avatar.jpg', { type: blob.type });

      // Optimize image to 200x200 WebP
      const optimizedBlob = await optimizeImageUpload(file, 200, 200, 0.85);

      const fileName = `${userId}/${Date.now()}.webp`;

      // Upload optimized image to storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, optimizedBlob, { 
          upsert: true,
          contentType: 'image/webp'
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Log audit event
      const { error: auditError } = await supabase.rpc('log_audit_event', {
        p_actor_id: userId,
        p_org_id: null,
        p_action: 'profile.avatar.upload',
        p_resource_type: 'profile',
        p_resource_id: userId,
        p_metadata: {
          file_name: fileName,
          file_size: optimizedBlob.size,
          optimized: true,
          dimensions: '200x200'
        }
      });

      if (auditError) {
        console.error('Audit log error:', auditError);
      }

      setAvatarUrl(publicUrl);
      setPreviewUrl(null);
      onAvatarUpdate?.(publicUrl);

      toast({
        title: 'Avatar updated',
        description: 'Your profile picture has been updated',
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload avatar',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const cancelPreview = () => {
    setPreviewUrl(null);
  };

  const removeAvatar = async () => {
    try {
      setUploading(true);

      // Update profile to remove avatar
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', userId);

      if (error) throw error;

      // Log audit event
      const { error: auditError } = await supabase.rpc('log_audit_event', {
        p_actor_id: userId,
        p_org_id: null,
        p_action: 'profile.avatar.remove',
        p_resource_type: 'profile',
        p_resource_id: userId,
        p_metadata: {
          previous_url: avatarUrl
        }
      });

      if (auditError) {
        console.error('Audit log error:', auditError);
      }

      setAvatarUrl(null);
      setPreviewUrl(null);
      onAvatarUpdate?.(null);

      toast({
        title: 'Avatar removed',
        description: 'Your profile picture has been removed',
      });
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast({
        title: 'Remove failed',
        description: error instanceof Error ? error.message : 'Failed to remove avatar',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <Avatar className="h-20 w-20">
        <AvatarImage src={previewUrl || avatarUrl || undefined} alt={userDisplayName || 'User'} />
        <AvatarFallback className="text-xl font-semibold">
          {getInitials()}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex flex-col gap-2">
        {previewUrl ? (
          <>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={uploadAvatar}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Confirm Upload
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={cancelPreview}
              disabled={uploading}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Label htmlFor="avatar-upload" className="cursor-pointer">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                asChild
              >
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  Select Image
                </span>
              </Button>
            </Label>
            <Input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
              className="hidden"
            />
            
            {avatarUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={removeAvatar}
                disabled={uploading}
              >
                <X className="h-4 w-4 mr-2" />
                Remove
              </Button>
            )}
          </>
        )}
        
        <p className="text-xs text-muted-foreground">
          {previewUrl 
            ? 'Image will be optimized to 200x200px' 
            : 'Max 2MB • JPG, PNG, WEBP, GIF'}
        </p>
      </div>
    </div>
  );
};
